import React, { useLayoutEffect, useRef, useState } from 'react'
import Popover from '@mui/material/Popover'

import { SIZES } from '~/common/globalStyles'
import Icon, { IconProps } from '~/views/components/ui/Icon'
import IconButton from '~/views/components/ui/IconButton'

interface Props {
  iconColor?: IconProps['color']
  iconName: IconProps['name']
  iconSize?: IconProps['width']

  onToggle(event: React.MouseEvent): void

  open: boolean
  className?: string
  style?: React.CSSProperties
  'data-testid'?: string
  children?: React.ReactNode
}

const IconButtonPopover: React.FC<Props> = ({
  children,
  iconColor,
  iconName,
  iconSize = SIZES.xl,
  onToggle,
  open,
  'data-testid': dataTestId,
  ...rest
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setAnchorEl(null)
      return
    }
    setAnchorEl((prev) => prev ?? buttonRef.current)
  }, [open])

  const handleOpen = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget)
    onToggle(event)
  }

  return (
    <>
      <IconButton
        ref={buttonRef}
        data-testid={dataTestId}
        {...rest}
        onClick={handleOpen}
        size="large"
      >
        <Icon name={iconName} width={iconSize} color={iconColor} />
      </IconButton>
      <Popover
        onClose={onToggle}
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {children}
      </Popover>
    </>
  )
}

export default IconButtonPopover
